// WHAT: Hook for manually generating contextual insights from health and productivity data
// WHY:  Provides interface for triggering insight generation with indicator data access
// DOMAIN: Core → Context Intelligence
// DEPENDENCIES: InsightGenerationService, health/productivity adapters, rule engine
// RISKS:
//   - NOTE: Multiple useEffect dependencies may cause unnecessary re-renders
//   - NOTE: Console.log statements should be replaced with proper logging
//   - NOTE: Error handling may not provide user-friendly feedback
//   - NOTE: Refresh delay (1s) may not be sufficient for all database updates

import { useState, useCallback, useEffect } from "react";

import { useHealthIndicatorData } from "../adapters/healthAdapter";
import { useProductivityIndicatorData } from "../adapters/productivityAdapter";
import { createIndicatorValueGetter } from "../engine/ruleEngine";
import { initializeIndicatorRegistry } from "../registry/indicatorRegistry";
import { initializeRelationshipRegistry } from "../registry/relationshipRegistry";
import { initializeRuleRegistry } from "../registry/ruleRegistry";
import { InsightGenerationService } from "../services/insightGenerationService";

/**
 * WHAT:
 *   Hook that provides interface for manually generating contextual insights
 *   from health and productivity indicator data.
 *
 * WHY:
 *   Enables components to trigger insight generation on demand with access to
 *   current indicator values.
 *
 * BEHAVIOR:
 *   - Initializes context intelligence registries on mount
 *   - Fetches indicator data from health and productivity adapters
 *   - Provides generateInsights function that processes rules
 *   - Manages generation state (isGenerating, error, lastGenerated)
 *   - Returns hasData flag to indicate if data is available
 *
 * RISKS:
 *   - NOTE: Multiple useEffect dependencies may cause unnecessary re-renders
 *   - NOTE: Console.log statements should be replaced with proper logging
 *   - NOTE: Error handling may not provide user-friendly feedback
 *   - NOTE: Refresh delay (1s) may not be sufficient for all database updates
 *
 * @returns Object with generation state and functions
 *
 */
export function useInsightGenerator() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastGenerated, setLastGenerated] = useState<null | number>(null);

  // NOTE: Ensure registries are initialized
  useEffect(() => {
    initializeIndicatorRegistry();
    initializeRelationshipRegistry();
    initializeRuleRegistry();
  }, []);

  // NOTE: Get indicator values using hooks
  const readinessData = useHealthIndicatorData("oura_readiness");
  const activityData = useHealthIndicatorData("oura_activity");
  const sleepData = useHealthIndicatorData("oura_sleep_score");
  const stepsData = useHealthIndicatorData("oura_steps");

  const taskCompletionData = useProductivityIndicatorData("timetracq_task_completion_rate");
  const openTasksData = useProductivityIndicatorData("timetracq_open_tasks");
  const totalTasksData = useProductivityIndicatorData("timetracq_total_tasks");

  /**
   * WHAT:
   *   Gets health indicator value by ID.
   *
   * WHY:
   *   Provides mapping from indicator ID to actual value.
   */
  const getHealthValue = useCallback(
    (indicatorId: string): null | number => {
      switch (indicatorId) {
        case "oura_readiness": {
          return readinessData.value;
        }
        case "oura_activity": {
          return activityData.value;
        }
        case "oura_sleep_score": {
          return sleepData.value;
        }
        case "oura_steps": {
          return stepsData.value;
        }
        default: {
          return null;
        }
      }
    },
    [readinessData.value, activityData.value, sleepData.value, stepsData.value],
  );

  /**
   * WHAT:
   *   Gets productivity indicator value by ID.
   *
   * WHY:
   *   Provides mapping from indicator ID to actual value.
   */
  const getProductivityValue = useCallback(
    (indicatorId: string): null | number => {
      switch (indicatorId) {
        case "timetracq_task_completion_rate": {
          return taskCompletionData.value;
        }
        case "timetracq_open_tasks": {
          return openTasksData.value;
        }
        case "timetracq_total_tasks": {
          return totalTasksData.value;
        }
        default: {
          return null;
        }
      }
    },
    [taskCompletionData.value, openTasksData.value, totalTasksData.value],
  );

  const generateInsights = useCallback(async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const getIndicatorValue = createIndicatorValueGetter(getHealthValue, getProductivityValue);
      const result = await InsightGenerationService.processRules(getIndicatorValue);

      setLastGenerated(Date.now());
      return result;
    } catch (error_) {
      const nextError = error_ instanceof Error ? error_ : new Error("Failed to generate insights");
      setError(nextError);
      throw nextError;
    } finally {
      setIsGenerating(false);
    }
  }, [
    getHealthValue,
    getProductivityValue,
    readinessData.value,
    activityData.value,
    sleepData.value,
    stepsData.value,
    taskCompletionData.value,
    openTasksData.value,
    totalTasksData.value,
  ]);

  return {
    error,
    generateInsights,
    hasData: readinessData.value !== null || taskCompletionData.value !== null,
    isGenerating,
    lastGenerated,
  };
}
